import React, { useState, useMemo } from 'react';
import {
  Search,
  Filter,
  MapPin,
  Star,
  ShieldCheck,
  CheckCircle2,
  Heart,
  SlidersHorizontal,
  X,
  Calendar,
} from 'lucide-react';
import { DashboardLayout } from '../components/dashboard/DashboardLayout';
import { COMPANIONS_DATA, isDemoCompanionProfile, getDemoCompanionBookingMessage } from '../data/companionsData';
import { CompanionCard } from '../components/CompanionCard';
import { CompanionDetailModal } from '../components/CompanionDetailModal';
import { BookingConfirmationModal } from '../components/BookingConfirmationModal';
import { Companion, BookingDetails } from '../types';
import { saveRealMeetup } from '../utils/meetupStorage';
import { useAuth } from '../context/AuthContext';
import { calculateBookingPricing } from '../utils/pricingStructure';
import { useLanguage } from '../i18n/LanguageContext';

export const CompanionsPage: React.FC = () => {
  const { userProfile } = useAuth();
  const { language, t } = useLanguage();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGender, setSelectedGender] = useState<'all' | 'female' | 'male'>('all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [sortBy, setSortBy] = useState<'recommended' | 'price-asc' | 'price-desc' | 'rating'>('recommended');
  const [wishlist, setWishlist] = useState<string[]>([]);
  const [demoBookingNotice, setDemoBookingNotice] = useState<string | null>(null);

  // Modals
  const [selectedCompanion, setSelectedCompanion] = useState<Companion | null>(null);
  const [activeBooking, setActiveBooking] = useState<BookingDetails | null>(null);
  const [bookingSuccessData, setBookingSuccessData] = useState<{ booking: BookingDetails; otp: string } | null>(null);

  // Active verified companions filtering
  const companionsList = useMemo(() => {
    return COMPANIONS_DATA.map((c) => ({
      ...c,
      companion_profile_status: c.companion_profile_status || 'Active',
      payment_verified: c.payment_verified ?? true,
    })).filter((c) => c.companion_profile_status === 'Active');
  }, []);

  const cities = useMemo(() => {
    const list = Array.from(new Set(companionsList.map((c) => c.city.split(',')[0].trim())));
    return ['all', ...list];
  }, [companionsList]);

  const filteredCompanions = useMemo(() => {
    return companionsList
      .filter((comp) => {
        // Gender filter
        if (selectedGender !== 'all' && comp.gender !== selectedGender) {
          return false;
        }

        // City filter
        if (selectedCity !== 'all' && !comp.city.toLowerCase().includes(selectedCity.toLowerCase())) {
          return false;
        }

        // Search query
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase();
          const matchesName = comp.name.toLowerCase().includes(q);
          const matchesCity = comp.city.toLowerCase().includes(q);
          const matchesInterests = comp.interests?.some((t) => t.toLowerCase().includes(q));
          const matchesBio = comp.bio?.toLowerCase().includes(q);
          if (!matchesName && !matchesCity && !matchesInterests && !matchesBio) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'price-asc') return a.hourlyRate - b.hourlyRate;
        if (sortBy === 'price-desc') return b.hourlyRate - a.hourlyRate;
        if (sortBy === 'rating') return b.rating - a.rating;
        return 0;
      });
  }, [companionsList, selectedGender, selectedCity, searchQuery, sortBy]);

  const toggleWishlist = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setWishlist((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const handleBookNow = (comp: Companion, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDemoCompanionProfile(comp)) {
      setDemoBookingNotice('Not available on this time / date');
      return;
    }
    const dateStr = new Date().toISOString().split('T')[0];
    const timeStr = '18:00';
    const pricing = calculateBookingPricing(comp, 'hourly', 2);

    setActiveBooking({
      companion: comp,
      date: dateStr,
      time: timeStr,
      durationHours: pricing.durationHours,
      bookingTier: 'hourly',
      packageTitle: pricing.packageTitle,
      occasion: 'Social Gathering',
      serviceCategory: 'Social Gathering',
      city: comp.city,
      meetingLocation: `${comp.city} - Public Cafe/Lounge`,
      baseAmount: pricing.baseAmount,
      platformFee: pricing.platformFee,
      companionPayout: pricing.companionPayout,
      taxes: pricing.taxes,
      totalAmount: pricing.totalPayable,
    });
  };

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedGender('all');
    setSelectedCity('all');
    setSortBy('recommended');
  };

  return (
    <DashboardLayout
      title={t('companions.title')}
      subtitle={t('companions.subtitle')}
      requireAuth={false}
    >
      <div className="space-y-6">
        {demoBookingNotice && (
          <div className="p-4 rounded-2xl bg-amber-50 border border-amber-300 text-amber-950 flex items-start justify-between gap-3 shadow-xs">
            <div className="space-y-1">
              <strong className="block text-xs font-black uppercase tracking-wider text-amber-800">
                Not available on this time / date
              </strong>
              <p className="text-xs font-semibold leading-relaxed">{demoBookingNotice}</p>
            </div>
            <button
              type="button"
              onClick={() => setDemoBookingNotice(null)}
              className="text-amber-700 hover:text-amber-950 p-1 rounded-lg cursor-pointer shrink-0"
              aria-label="Close notice"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {/* Search & Filter Controls Bar */}
        <div className="p-4 sm:p-6 bg-white rounded-3xl border border-slate-200 shadow-xs space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search input */}
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search companions by name, interest, or city..."
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-xs text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 outline-hidden"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* City Selector */}
            <div className="sm:w-48">
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-700 bg-white focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 outline-hidden"
              >
                <option value="all">All Locations</option>
                {cities.filter((c) => c !== 'all').map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort Dropdown */}
            <div className="sm:w-48">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-700 bg-white focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 outline-hidden"
              >
                <option value="recommended">Featured / Best Match</option>
                <option value="rating">Highest Rated</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
              </select>
            </div>
          </div>

          {/* Gender Buttons */}
          <div className="flex items-center justify-between flex-wrap gap-2 pt-1 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-500">I am looking for:</span>
              {(['all', 'female', 'male'] as const).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setSelectedGender(g)}
                  className={`px-3 py-1 rounded-full text-xs font-bold capitalize transition-all cursor-pointer ${
                    selectedGender === g
                      ? 'bg-slate-900 text-white shadow-xs'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {g === 'all' ? 'All Companions' : g === 'female' ? 'Girlfriends / Hostesses' : 'Boyfriends / Hosts'}
                </button>
              ))}
            </div>

            <div className="text-xs text-slate-400 font-semibold">
              Showing <strong>{filteredCompanions.length}</strong> verified hosts
            </div>
          </div>
        </div>

        {/* Companions Grid or Empty State */}
        {filteredCompanions.length === 0 ? (
          <div className="p-12 text-center bg-white rounded-3xl border border-dashed border-slate-300 space-y-4 shadow-xs">
            <div className="w-16 h-16 rounded-2xl bg-pink-50 text-pink-600 flex items-center justify-center mx-auto">
              <Search className="w-8 h-8" />
            </div>
            <div className="space-y-1 max-w-md mx-auto">
              <h3 className="text-base font-black text-slate-900">No Companions Found</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                We couldn't find any companions matching your active filters. Try expanding your search terms or clearing your location filter.
              </p>
            </div>
            <div className="pt-2">
              <button
                type="button"
                onClick={resetFilters}
                className="px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl shadow-xs transition-colors cursor-pointer"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {filteredCompanions.map((companion) => (
              <CompanionCard
                key={companion.id}
                companion={companion}
                isWishlisted={wishlist.includes(companion.id)}
                onToggleWishlist={toggleWishlist}
                onSelect={setSelectedCompanion}
                onBookNow={handleBookNow}
              />
            ))}
          </div>
        )}
      </div>

      {/* Companion Detail Modal */}
      {selectedCompanion && (
        <CompanionDetailModal
          companion={selectedCompanion}
          onClose={() => setSelectedCompanion(null)}
          onProceedToBooking={(booking) => {
            setSelectedCompanion(null);
            if (isDemoCompanionProfile(booking.companion)) {
              setDemoBookingNotice('Not available on this time / date');
              return;
            }
            setActiveBooking(booking);
          }}
          isWishlisted={wishlist.includes(selectedCompanion.id)}
          onToggleWishlist={toggleWishlist}
        />
      )}

      {/* Booking Confirmation / Booking Form Modal */}
      {activeBooking && (
        <BookingConfirmationModal
          booking={activeBooking}
          onClose={() => setActiveBooking(null)}
          onSuccess={() => {
            setActiveBooking(null);
          }}
        />
      )}

      {/* Booking Success Notification Card */}
      {bookingSuccessData && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl space-y-4 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <div className="space-y-1">
              <h3 className="text-xl font-black text-slate-900">Booking Confirmed!</h3>
              <p className="text-xs text-slate-600">
                Your appointment with <strong>{bookingSuccessData.booking.companion.name}</strong> has been secured in escrow.
              </p>
            </div>

            <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200 text-center space-y-1">
              <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wide">
                Your Secret 4-Digit In-Person OTP
              </span>
              <div className="text-3xl font-black font-mono tracking-widest text-amber-950">
                {bookingSuccessData.otp}
              </div>
              <p className="text-[10px] text-amber-700">
                Give this OTP to your companion during the meetup to release payment from escrow.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setBookingSuccessData(null)}
              className="w-full py-3 bg-pink-600 hover:bg-pink-700 text-white font-bold text-xs rounded-xl shadow-xs transition-colors cursor-pointer"
            >
              Done & View Bookings
            </button>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};
